import { TFunction } from 'i18next';
import { EntityTypes } from 'models/entity';
import { CopilotFieldRef, hasAIFillInField } from '../copilotField';
import { AIActionBinding, createAIActionBinding } from './aiActionBinding';
import { AIActionKind, AIPromptActionTemplate } from './aiActionTemplate';
import { askAssistantTemplate } from './aiActionTemplates';

/**
 * A rich-text field as a text action sees it: which entity field it is and how the user knows it.
 *
 * The text templates are Prompt kinds, so they land in the Assistant group of the field's AI menu and
 * run in the Copilot panel: the agent reads the entity's other texts server-side, works on the text the
 * editor holds (it travels with the request as the active field) and answers with a proposal card the
 * user applies in the editor. That is why there is no local callout: the field needs the entity
 * context, the same confirmation card serves every field, and a free question ("Ask AI") comes for
 * free. One `text.improve` template therefore serves every bound field.
 */
export type AITextFieldParams = {
  field: CopilotFieldRef;
  // the field's label as the scene shows it ("Threat"), for the prompt text
  fieldLabel: string;
  // the entity type in the user's language ("Risk"), for the prompt text
  entityLabel: string;
};

// one binding per field: two fields of one entity must not share an id
const getTextAnchor = (params: AITextFieldParams): string =>
  `${EntityTypes[params.field.entityType]}:${params.field.entityId}:${params.field.field}`;

// Writes the field from the entity's other texts. Bound only when the server has a question for the pair.
export const textFillInTemplate: AIPromptActionTemplate<AITextFieldParams> = {
  id: 'text.fillIn',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Text.FillIn.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return t('copilot:Actions.Text.FillIn.Prompt', { field: params.fieldLabel, entity: params.entityLabel });
  },
  getAnchor(params) {
    return getTextAnchor(params);
  },
};

export const textImproveTemplate: AIPromptActionTemplate<AITextFieldParams> = {
  id: 'text.improve',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Text.Improve.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return t('copilot:Actions.Text.Improve.Prompt', { field: params.fieldLabel, entity: params.entityLabel });
  },
  getAnchor(params) {
    return getTextAnchor(params);
  },
};

export const textSimplifyTemplate: AIPromptActionTemplate<AITextFieldParams> = {
  id: 'text.simplify',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Text.Simplify.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return t('copilot:Actions.Text.Simplify.Prompt', { field: params.fieldLabel, entity: params.entityLabel });
  },
  getAnchor(params) {
    return getTextAnchor(params);
  },
};

export const textSummarizeTemplate: AIPromptActionTemplate<AITextFieldParams> = {
  id: 'text.summarize',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Text.Summarize.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return t('copilot:Actions.Text.Summarize.Prompt', { field: params.fieldLabel, entity: params.entityLabel });
  },
  getAnchor(params) {
    return getTextAnchor(params);
  },
};

export const textExpandTemplate: AIPromptActionTemplate<AITextFieldParams> = {
  id: 'text.expand',
  kind: AIActionKind.Prompt,
  getLabel(t: TFunction<string[]>) {
    return t('copilot:Actions.Text.Expand.Label');
  },
  getPrompt(t: TFunction<string[]>, params) {
    return t('copilot:Actions.Text.Expand.Prompt', { field: params.fieldLabel, entity: params.entityLabel });
  },
  getAnchor(params) {
    return getTextAnchor(params);
  },
};

const textRewriteTemplates: AIPromptActionTemplate<AITextFieldParams>[] = [
  textImproveTemplate,
  textSimplifyTemplate,
  textSummarizeTemplate,
  textExpandTemplate,
];

/**
 * What one rich-text field offers, in menu order: fill-in when the server has a question for the pair
 * and the entity is saved, the four rewrites when there is text to rewrite, and the free question. The
 * prompts need the general knowledge chat; without it only the free question remains.
 */
export const getTextFieldBindings = (
  params: AITextFieldParams,
  options: { hasText: boolean; chatAvailable: boolean },
): AIActionBinding[] => {
  const bindings: AIActionBinding[] = [];

  if (options.chatAvailable) {
    if (params.field.entityId > 0 && hasAIFillInField(params.field)) {
      bindings.push(createAIActionBinding(textFillInTemplate, params));
    }
    for (const template of textRewriteTemplates) {
      bindings.push(createAIActionBinding(template, params, { disabled: !options.hasText }));
    }
  }

  bindings.push(createAIActionBinding(askAssistantTemplate, undefined));

  return bindings;
};
